import { inject, Injectable, InjectionToken } from '@angular/core';
import type { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import type { BleConnectionState, BleDeviceInfoExtended, BleServiceResponse, DeviceData } from '../../interfaces/ble.interface';
import { MockBleDeviceManagerService } from '../../../mocks/hunting-device/mock-ble-device-manager.service';
import { MockHuntingDeviceGate } from '../../../mocks/hunting-device/mock-hunting-device-gate.service';

import type { BleDeviceManagerService } from './ble-device-manager.service';

// Separate token for the real manager so the facade can hold a real instance while the app-wide
// BleDeviceManagerService token resolves to the facade itself (see app.config.ts).
export const REAL_BLE_MANAGER = new InjectionToken<BleDeviceManagerService>('REAL_BLE_MANAGER');

/**
 * Routes every BLE call/stream to the real manager or the mock based on MockHuntingDeviceGate, so
 * the dev toggle swaps them at runtime instead of build time. Deliberately does not extend
 * BleDeviceManagerService (that would re-run its BLE-initializing constructor).
 */
@Injectable()
export class BleDeviceManagerFacade {
  // services
  private readonly real = inject(REAL_BLE_MANAGER);
  private readonly mock = inject(MockBleDeviceManagerService);
  private readonly gate = inject(MockHuntingDeviceGate);

  // public observables
  public readonly connectionState$: Observable<BleConnectionState> = this.gate.active$.pipe(
    switchMap(active => (active ? this.mock.connectionState$ : this.real.connectionState$))
  );
  public readonly connectedDevice$: Observable<BleDeviceInfoExtended | undefined> = this.gate.active$.pipe(
    switchMap(active => (active ? this.mock.connectedDevice$ : this.real.connectedDevice$))
  );
  public readonly deviceData$: Observable<DeviceData> = this.gate.active$.pipe(switchMap(active => (active ? this.mock.deviceData$ : this.real.deviceData$)));
  public readonly scannedDevices$: Observable<Array<BleDeviceInfoExtended>> = this.gate.active$.pipe(
    switchMap(active => (active ? this.mock.scannedDevices$ : this.real.scannedDevices$))
  );
  public readonly bleEnabled$: Observable<boolean> = this.gate.active$.pipe(switchMap(active => (active ? this.mock.bleEnabled$ : this.real.bleEnabled$)));

  public scanForDevices$(): Observable<void> {
    return this.gate.isActive ? this.mock.scanForDevices$() : this.real.scanForDevices$();
  }

  public connectToDevice$(deviceId: string): Observable<BleServiceResponse<BleDeviceInfoExtended>> {
    return this.gate.isActive ? this.mock.connectToDevice$(deviceId) : this.real.connectToDevice$(deviceId);
  }

  public disconnect$(): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.disconnect$() : this.real.disconnect$();
  }

  public readBatteryLevel$(): Observable<BleServiceResponse<number>> {
    return this.gate.isActive ? this.mock.readBatteryLevel$() : this.real.readBatteryLevel$();
  }

  public readBrightnessLevel$(): Observable<BleServiceResponse<number>> {
    return this.gate.isActive ? this.mock.readBrightnessLevel$() : this.real.readBrightnessLevel$();
  }

  public writeBrightnessLevel$(level: number): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.writeBrightnessLevel$(level) : this.real.writeBrightnessLevel$(level);
  }

  public writeContrastLevel$(level: number): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.writeContrastLevel$(level) : this.real.writeContrastLevel$(level);
  }

  public writeImageMode$(mode: number): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.writeImageMode$(mode) : this.real.writeImageMode$(mode);
  }

  public writeConnectivityEnabled$(enabled: boolean): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.writeConnectivityEnabled$(enabled) : this.real.writeConnectivityEnabled$(enabled);
  }

  public writeWifiEnabled$(enabled: boolean): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.writeWifiEnabled$(enabled) : this.real.writeWifiEnabled$(enabled);
  }

  public readAccessPointSSID$(): Observable<BleServiceResponse<string>> {
    return this.gate.isActive ? this.mock.readAccessPointSSID$() : this.real.readAccessPointSSID$();
  }

  public writeStreamStart$(): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.writeStreamStart$() : this.real.writeStreamStart$();
  }

  public readStreamingStatus$(): Observable<BleServiceResponse<boolean>> {
    return this.gate.isActive ? this.mock.readStreamingStatus$() : this.real.readStreamingStatus$();
  }

  public writeAccessPointPassphrase$(passphrase: string): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.writeAccessPointPassphrase$(passphrase) : this.real.writeAccessPointPassphrase$(passphrase);
  }

  public writeWifiMode$(mode: number): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.writeWifiMode$(mode) : this.real.writeWifiMode$(mode);
  }

  public loadSettingsData$(): Observable<{ brightnessLevel: number; wifiEnabled: boolean; contrastLevel: number; imageMode: number }> {
    return this.gate.isActive ? this.mock.loadSettingsData$() : this.real.loadSettingsData$();
  }

  public readDeviceInfo$(): Observable<BleServiceResponse<Partial<DeviceData>>> {
    return this.gate.isActive ? this.mock.readDeviceInfo$() : this.real.readDeviceInfo$();
  }

  public loadInitialData$(): Observable<void> {
    return this.gate.isActive ? this.mock.loadInitialData$() : this.real.loadInitialData$();
  }

  public readBleLocalName$(): Observable<BleServiceResponse<string>> {
    return this.gate.isActive ? this.mock.readBleLocalName$() : this.real.readBleLocalName$();
  }

  public writeBleLocalName$(name: string): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.writeBleLocalName$(name) : this.real.writeBleLocalName$(name);
  }

  public stopScan(): Promise<void> {
    return this.gate.isActive ? this.mock.stopScan() : this.real.stopScan();
  }

  public get isConnected(): boolean {
    return this.gate.isActive ? this.mock.isConnected : this.real.isConnected;
  }

  public get currentConnectionState(): BleConnectionState {
    return this.gate.isActive ? this.mock.currentConnectionState : this.real.currentConnectionState;
  }
}

// Compile-time guard: fails the build if the facade drifts from the real manager's public API.
type _PublicApi = Pick<
  BleDeviceManagerService,
  | 'connectionState$'
  | 'connectedDevice$'
  | 'deviceData$'
  | 'scannedDevices$'
  | 'bleEnabled$'
  | 'scanForDevices$'
  | 'connectToDevice$'
  | 'disconnect$'
  | 'readBatteryLevel$'
  | 'readBrightnessLevel$'
  | 'writeBrightnessLevel$'
  | 'writeContrastLevel$'
  | 'writeImageMode$'
  | 'writeConnectivityEnabled$'
  | 'writeWifiEnabled$'
  | 'readAccessPointSSID$'
  | 'writeStreamStart$'
  | 'readStreamingStatus$'
  | 'writeAccessPointPassphrase$'
  | 'writeWifiMode$'
  | 'loadSettingsData$'
  | 'readDeviceInfo$'
  | 'loadInitialData$'
  | 'readBleLocalName$'
  | 'writeBleLocalName$'
  | 'stopScan'
  | 'isConnected'
  | 'currentConnectionState'
>;
const _assert: _PublicApi = undefined as unknown as BleDeviceManagerFacade;
void _assert;

// Fails the build when the real manager gains a public member the facade does not forward —
// a missing forward otherwise surfaces only as a runtime TypeError (the app-wide token
// resolves to the facade).
type _MissingFromFacade = Exclude<keyof BleDeviceManagerService, keyof BleDeviceManagerFacade>;
const _assertNoneMissing: Record<_MissingFromFacade, never> = {};
void _assertNoneMissing;
